import { useTranslation } from "react-i18next";

import { FileInput, Loader, PasswordInput, Select } from "@mantine/core";

import { KeyIcon } from "@prostocheck/controls-web";
import { Button } from "@prostocheck/ui";

import { KEY_FILE_ACCEPT } from "../../login.constants";
import { toAuthorityOptions } from "../../login.utils";
import { StepHeading } from "../step-heading";
import { IKeyStepProps } from "./key-step.models";

/**
 * Step one: the container, its password and the ЦСК that issued it. Nothing
 * here leaves the browser — the file is read and decrypted in the page.
 */
export const KeyStep = ({
  form,
  authorities,
  isBusy,
  detectProgress,
  error,
  onSubmit,
}: IKeyStepProps) => {
  const { t } = useTranslation();

  const busyLabel = detectProgress
    ? t("login.key.detecting", {
        checked: detectProgress.checked,
        total: detectProgress.total,
      })
    : t("login.key.reading");
  const submitLabel = isBusy ? busyLabel : t("login.key.submit");

  return (
    <>
      <StepHeading
        icon={<KeyIcon className="size-5.5" />}
        title={t("login.key.title")}
        description={t("login.key.description")}
      />

      <form onSubmit={form.onSubmit(onSubmit)} className="mt-7">
        <div className="space-y-5">
          <Select
            size="md"
            radius="md"
            searchable
            label={t("login.key.authorityLabel")}
            placeholder={t("login.key.authorityPlaceholder")}
            description={t("login.key.authorityHint")}
            nothingFoundMessage={t("login.key.authorityNotFound")}
            data={toAuthorityOptions(authorities, t)}
            {...form.getInputProps("cmpUrl")}
          />

          <FileInput
            size="md"
            radius="md"
            accept={KEY_FILE_ACCEPT}
            label={t("login.key.fileLabel")}
            placeholder={t("login.key.filePlaceholder")}
            clearable
            {...form.getInputProps("file")}
          />

          <PasswordInput
            size="md"
            radius="md"
            label={t("login.key.passwordLabel")}
            {...form.getInputProps("password")}
          />

          {error}

          <Button type="submit" size="lg" className="w-full" disabled={isBusy}>
            {isBusy && <Loader size="xs" color="white" />}
            {submitLabel}
          </Button>
        </div>
      </form>
    </>
  );
};
